import React from 'react';

import { colors } from '@styles/theme-chakra';
import zjcImage from '../../../assets/guides/callouts/zjc.png';
import mmImage from '../../../assets/guides/callouts/mm.png';
import toolsImage from '../../../assets/guides/callouts/tools.png';
import toolsImageMobile from '../../../assets/guides/callouts/toolsmobile.png';
import zjcMobile from '../../../assets/guides/callouts/zjcmobile.png';
import mmMobile from '../../../assets/guides/callouts/mmmobile.png';
import FarnooshAvatar from '../../../assets/guides/combining-finances/farnoosh.png';
import FarnooshAvatarMobile from '../../../assets/guides/combining-finances/farnoosh_mobile.png';
import ElleAvatar from '../../../assets/guides/combining-finances/elle.png';
import ElleAvatarMobile from '../../../assets/guides/combining-finances/elle_mobile.png';

export const TopSection = [
  {
    n: 1,
    title: 'Things to consider',
    lineItems: [
      {
        item: 'Discuss these questions before selecting a merge model',
        href: '#things-to-consider'
      },
      {
        item: 'How do other couples manage their finances?',
        href: '#example-couple'
      }
    ]
  },
  {
    n: 2,
    title: 'Models for combining finances',
    lineItems: [
      {
        item: 'How should we split expenses if we earn different incomes?',
        href: '#split-expenses'
      },
      {
        item: 'How should we plan for a single income life?',
        href: '#single-life-income'
      }
    ]
  }
];

export const BottomSection = [
  {
    n: 3,
    title: 'Varied or single income couples',
    lineItems: [
      { item: 'All in it together', href: '#all-in-it' },
      { item: 'Keeping it separate', href: '#keeping-it-separate' },
      { item: 'Yours, mine, ours', href: '#yours-mine-ours' }
    ]
  },
  {
    n: 4,
    title: 'Marriage law 101',
    lineItems: [
      { item: 'Marriage law 101', href: '#marriage-law' },
      { item: 'Should we get a pre/post nup agreement?', href: '#pre-post-nuptial' }
    ]
  }
];

export const ZetaJointCards = {
  heading: `Meet Zeta's Joint Cards`,
  subheading: `Shared Banking for Modern Couples`,
  body: `Two cards. One joint account. Built-in features built to help your relationship thrive.`,
  buttonText: 'Get started',
  image: zjcImage,
  mobileImage: zjcMobile,
  link: 'https://www.askzeta.com/joint-bank-account'
};

export const ZetaTools = {
  heading: `Meet Zeta's Tools`,
  subheading: `Master Money as a Team`,
  body: `Zeta offers financial tools that help your relationship thrive.`,
  buttonText: 'Learn more',
  image: toolsImage,
  mobileImage: toolsImageMobile,
  link: 'https://www.askzeta.com/'
};

export const ThingsToConsiderData = [
  {
    heading: 'OUR PAST',
    body: `<ul>
      <br />
      <li>&bull; What did you learn from the way your parents approached their finances? Did you like or dislike something about their approach?</li>
      <br />
      <li>&bull; Is there anything in either of your histories that might make you uncomfortable with money?</li>
      </ul>`,
    key: 'past',
    containsHTML: true
  },
  {
    heading: 'OUR PRESENT',
    body: [
      `<ul>
      <br />
      <li>&bull; How do you currently handle your finances? What's working well? What's not working well?</li>
      <br />
      <li>&bull; Do you have similar or very different money personalities?</li>
      <br />
      <li>&bull; What does financial independence mean to you? How important is it to you?</li>
      <br />
      <li>&bull; How do the assets or debt that each of you have coming into this relationship change how much you want to combine your finances?</li> 
      </ul>`
    ],
    key: 'present',
    containsHTML: true
  },
  {
    heading: 'OUR FUTURE',
    body: [
      `<ul>
      <br />
      <li>&bull; What has your expectation been regarding whether you'd want to merge your money or keep it separate from your partner?</li>
      <br />
      <li>&bull; If you chose "Model X", how would that change if you moved in together/got married/had a baby?</li>
      <br />
      <li>&bull; In the event that you separate, how would you want to handle your finances?</li>
      <br />
      <li>&bull; Do you expect to get any inheritance from your family? How would you want to use that money?
      </ul>`
    ],
    key: 'future',
    containsHTML: true
  }
];

export const ModelOneData = [
  {
    heading: 'WORKS WELL FOR',
    body: `<ul>
      <br />
      <li>&bull; Married couples, and especially for couples with kids. This is not recommended if you’re dating or engaged, because it’s often too soon to merge your finances fully. In the event of a breakup, your money would not be protected.</li>
      </ul>`,
    key: 'works',
    containsHTML: true
  },
  {
    heading: 'PROS & CONS',
    body: [
      `<ul>
      <br />
      <li>&bull; PRO: It's easy to setup and manage over time. Budgeting is a lot easier when you're treating your expenses and income as one.</li>
      <br />
      <li>&bull; PRO: Some couples have reported feeling closer together as a result of having combined finances — it offers full transparency into where your money is going and what your significant other is spending on.</li>
      <br />
      <li>&bull; CONS: It can create a messy situation in the case of a separation or divorce. Unless you’re comfortable just splitting your assets down the middle, it can be difficult to determine who contributed what many years down the line – which can then become a big source of contention in a divorce proceeding. (Read more on pre-nup agreements below).</li>
      <br />
      <li>&bull; CONS: It requires you to be 100% on the same page about how you want to manage your finances, which can be hard to achieve. Sometimes one partner’s passions may not match those of the other partner, which can become a source of ongoing tension in the relationship.</li> 
      </ul>`
    ],
    key: 'proscons',
    containsHTML: true
  },
  {
    heading: 'HOW TO IMPLEMENT THIS MODEL',
    body: [
      `<ul>
      <br />
      <li>&bull; Start by selecting which account/bank you prefer. You can use an existing bank account that one of your has, or start fresh. For more on how to set up a joint bank account, we recommend speaking with your preferred bank. The good news is that in most cases, it takes less than 10 minutes to create a joint bank account online.</li>
      <br />
      <li>&bull; We'd also recommend having a checking and a savings account where both your names are on the account.</li>
      <br />
      <li>&bull; *Pro Tip* Even if you’re merging everything, get a credit card in your own names (not the same as a joint credit card). This allows you to build individual credit history so that in the case of a divorce or your spouse's death, you’re not credit-less at a time when you might need it most.</li>
      </ul>`
    ],
    key: 'how',
    containsHTML: true
  }
];

export const ModelTwoData = [
  {
    heading: 'WORKS WELL FOR',
    body: `<ul>
      <br />
      <li>&bull; Couples who are living together, engaged, or married. This is also our number one tip for couples moving in together: along with planning for your move, you should consider setting up an “ours” bucket for your finances.</li>
      </ul>`,
    key: 'works',
    containsHTML: true
  },
  {
    heading: 'PROS & CONS',
    body: [
      `<ul>
      <br />
      <li>&bull; PRO: If a partner really values their financial autonomy, this model allows them to maintain that independence while easily managing and paying for shared expenses.</li>
      <br />
      <li>&bull; PRO: Partners report feeling less scrutiny over their own purchases since they're coming out of their individual accounts. For example, you have the ability to support other family members or buy large items out of your individual account without feeling like you’re spending your shared money.</li>
      <br />
      <li>&bull; PRO: It’s easier to pinpoint where your money is going (personal vs. shared spending) and what it’s going towards (eating out together vs. eating out by myself).</li>
      <br />
      <li>&bull; CONS: If you’re married, some relationship experts believe that having separated finances creates an unproductive distinction between "mine" and "yours."</li> 
      <br />
      <li>&bull; CONS: Separate accounts can create trust issues with your partner — especially if one partner likes to be in the know or is prone to suspicion.</li> 
      <br />
      <li>&bull; CONS: Some critics feel like certain pre-marital money (like student debt or mortgages) should be a shared burden, as you’re both benefiting from the fruits of those investments during your marriage.</li> 
      </ul>`
    ],
    key: 'proscons',
    containsHTML: true
  },
  {
    heading: 'HOW TO IMPLEMENT THIS MODEL',
    body: [
      `<ul>
      <br />
      <li>&bull; Estimate your shared expenses (rent, eating-out, travel) and set a target shared monthly budget. This will give you a sense of how much you need to cover together.</li>
      <br />
      <li>&bull; *Pro tip* Looking at your spending habits over the past few months can help you get a better estimate of how much you spend together monthly. You can always look at your spending patterns in Zeta by searching categories.</li>
      <br />
      <li>&bull; Then decide how much you each want to contribute to your shared expenses. Some couples choose to contribute a percentage of their income (eg. 80% of take home pay) while others may select a dollar amount (eg. $2,000/month).</li>
      <br />
      <li>&bull; If you make very different incomes, you can choose to contribute to your joint bank account equally or proportionally to your income. Check out our varied income section for more details on how to approach this.</li>
      <br />
      <li>&bull; Establish a few key shared accounts:
      Joint checking account - where you each contribute your portion of income.
      Joint savings account - pull any excess money into this account for your goals.
      Joint credit card - gives you each the ability to charge shared purchases easily.</li>
      <br />
      <li>&bull; *Pro tip* Make sure both your names are on your shared accounts and credit card. That way either of you can access your accounts in case of an emergency. It’s worth noting that this also means either of you can remove money or charge things to your credit card.</li>
      <br />
      <li>&bull; Once you've decided how much each of you is contributing, <a style="color:#195F53; text-decoration:underline;" href="https://www.askzeta.com/magazine/articles/automating-finances-couple-pros-cons/?utm_medium=content&utm_source=combining-finances-guide">automate your finances</a>! Set up a monthly recurring transfer after you get paid to fund your joint checking and savings accounts.</li>
      <br />
      <li>&bull; Hold a <a style="color:#195F53; text-decoration:underline;" href="https://www.askzeta.com/magazine/articles/why-every-couple-needs-money-date/?utm_medium=content&utm_source=combining-finances-guide">monthly money date</a>. Whether over a cup or coffee or a glass of wine, sit down with your partner to look at your monthly expenses and make adjustments to your monthly contributions as needed.</li>
      </ul>`
    ],
    key: 'how',
    containsHTML: true
  }
];

export const ModelThreeData = [
  {
    heading: 'WORKS WELL FOR',
    body: `<ul>
      <br />
      <li>&bull; Couples who are dating or living together.</li>
      </ul>`,
    key: 'works',
    containsHTML: true
  },
  {
    heading: 'PROS & CONS',
    body: [
      `<ul>
      <br />
      <li>&bull; PRO: Your finances are your own to manage and it doesn't require coordination with your partner.</li>
      <br />
      <li>&bull; CON: This model usually only works for so long. As your lives as a couple become more intertwined, it's inevitable that your finances will, too. However, there are certainly successful stories of couples who have kept things apart even after having kids!</li>
      <br />
      </ul>`
    ],
    key: 'proscons',
    containsHTML: true
  },
  {
    heading: 'HOW TO IMPLEMENT THIS MODEL',
    body: [
      `<ul>
      <br />
      <li>&bull; Keep your finances in your individual accounts.</li>
      <br />
      <li>&bull; Make a list of shared bills/responsibilities and divy them up between yourselves. You can either choose to trade-off paying for these bills or choose to split everything down the middle.</li>
      <br />
      <li>&bull; *Pro Tip* You can use Zeta to get a rough sense of how much you spend on each of these bills when divvying things up, and then track how much you’re spending on shared expenses moving forward.</li>
      <br />
      <li>&bull; If you’re choosing to split all your bills, the split expenses feature on Zeta helps you keep a running tally on how much you owe each other.</li>
      <br />
      <li>&bull; If you’re married or getting married, <a style="color:#195F53; text-decoration:underline;" href="https://www.askzeta.com/magazine/articles/prenup-relationship-coach-financial-emotional-risks/?utm_medium=content&utm_source=combining-finances-guide">get a pre/post nup</a>! A pre/post nup will help you keep your assets and liabilities legally separate in the way you initially intended. Not doing so would mean you’d be exposing your money and debts to each other even if they’re in individual accounts.</li>
      </ul>`
    ],
    key: 'how',
    containsHTML: true
  }
];

export const farnoosh = {
  first_name: 'Farnoosh',
  last_name: 'Torabi',
  description: `Farnoosh is a believer in the yours, mine, our approach - one she practices with her husband, Tim.`,
  bio_one: `
  She's also the breadwinner in their family, which incidentally propelled her into a career in personal finance. After realizing that higher-earning women faced a greater risk of burnout, infidelity and divorce, Farnoosh dove head-first into figuring out how best to handle this dynamic in a relationship.
`,
  bio_two: ` Based on her research with 1,300+ women, Farnoosh published her findings in her book: When She Makes More. You can learn more about Farnoosh and her hugely successful podcast, So Money.`,
  image: FarnooshAvatar,
  imageMobile: FarnooshAvatarMobile,
  link: 'https://farnoosh.tv/'
};

export const elle = {
  first_name: 'Elle',
  last_name: 'Martinez',
  bio_one: `Elle and her husband practice the single-income approach - a habit they started in the early days of their relationship to stay out of debt!`,
  bio_two: `Check out Elle's story on The Money Date Podcast, where we sit down with couples who talk us through how they manage their finances together.`,
  image: ElleAvatar,
  imageMobile: ElleAvatarMobile,
  link:
    'https://anchor.fm/themoneydate/episodes/The-Money-Date---Episode-5---Elle-Martinez-e224ih/a-a3rf0q'
};

export const SingleIncomeLife = {
  title: `How should we plan for a single-income life?`,
  heading: `
  Some couples choose the single-income life - a setup where they live on one of their incomes, funneling the second income directly towards savings (or debt payback). This is a pretty effective strategy if you can pull it off, helping you build strong financial habits early in your relationship. Some couples have even managed to maintain their single-income after having kids (ie. even when their expenses went ☝️dramatically). Doing so has put them ahead for retirement and their goals.`,
  body: [
    {
      title: `Understand your expenses.`,
      bio: `A single income life has to start with first understanding your expenses. Knowing how much you're trying to spend gives you a sense of whether you can even pursue a single-income strategy.  If you find that you can pull it off, decide which of your incomes you'll keep for your expenses and which one you'll direct towards savings/debt. `
    },
    {
      title: `Make your second income invisible.`,
      bio: `Once you know which income you're living on, make the second income totally invisible. If you're using it to pay down debt, have it automatically go towards that payment. If possible, don't deposit your second income into your checking account; instead send it directly towards a saving account. The goal here is to make yourselves forget that you've got this second income - you can't miss something you never had!`
    },
    {
      title: `Check-in regularly.`,
      bio: `It's good to circle up on your primary income weekly or monthly <a style="color:#195F53; text-decoration:underline;" href="https://www.askzeta.com/magazine/articles/why-every-couple-needs-money-date">(we’re fans of having a regular money date - here’s how)</a> and on your second income every 3-4 months. If you're using it to pay down debt, check to see if that still makes sense. If you're trying to invest it all, review the funds or the stocks you've invested it in.`
    }
  ]
};

export const VariedIncomeData = {
  title: `Varied or single income couples`,
  subtitle: `What if you don’t earn the same?`,
  heading: `Often, couples don't earn the same amount of money, with one partner earning significantly more or less. In this instance, there’s often a question about how these partners should manage their finances. Whether you're the <a style="color:#195F53; text-decoration:underline;" href="https://www.askzeta.com/magazine/articles/income-inequality-in-marriage-relationships?utm_medium=content&utm_source=combining-finances-guide">one earning more or less</a>, there is often some discomfort about what or how you might want to spend the money. If you have entirely merged finances, the lower earning partner could feel guilty about spending money they didn't earn, while the bigger earner could become resentful of contributing more. Whatever the situation, this is a delicate scenario that requires couples to get on the same page sooner rather than later — and communication is key.`,
  body: [
    {
      title: 'Start an open conversation.',
      body: `It’s important to discuss the imbalance up front - here are a few things to cover:
      <br />
      <br />
      Plan for how much you’ll spend on shared expenses together (here’s a <a style="color:#195F53; text-decoration:underline;" href="https://www.askzeta.com/magazine/articles/budgeting-for-two-how-to-build-a-budget-with-your-significant-other/?utm_medium=content&utm_source=combining-finances-guide">free tool</a> to help you build a budget together).
      Decide the percentage that each of you is contributing towards those shared expenses.
      Be clear on any stipulations you might have about how to handle that money (e.g. just for rent and groceries, or for anything we’ve ever wanted).`
    },
    {
      title: `Select one of these approaches.`,
      body: `
      <b><i>Merge everything together.</i></b> 
      <br />
      Some couples with greatly varied incomes still choose to merge their incomes completely. This approach especially makes sense if one partner is staying home to take care of the kids, is in school, or otherwise isn’t earning an income.
      <br />
      <br />
      <b><i>Split shared expenses 50/50.</i></b>
      <br />
      In this approach, couples contribute equally to a shared pool of money and pay for all expenses through this. It’s often easier to do this via a shared checking account, but it can also be done by trading off bills. This works well for two-income couples, but it has limitations when it comes to major expenses (like buying a home or going on a vacation). Some couples solve that problem by offering to do a proportional split for larger purchases but splitting their fixed expenses down the middle.
      <br />
      <br />
      <b><i>Split shared expenses proportionally to income.</i></b>
      <br />
      Couples with this split each contribute a % of their income towards shared expenses. If Partner 1 earns $50,000 and Partner 2 earns $100,000, this means that P1 will pay for 33% of their shared expenses and P2 will pay for 66% of their shared expenses. This approach works well if the higher earning partner wants to spend more without making the other feel like they’re living beyond their means.
      `
    },
    {
      title: 'Automate your system.',
      body: `Once you know how you’ll be doing things, it’s time to make it run like clockwork. Our happiest couples have set up their accounts to directly transfer money into their shared accounts when they’re paid. And they use Zeta to make sure they’re on track towards their monthly budget. This way, the system works without them having to lift a finger. For any unforeseen expenses, couples can make one-time transfers into their shared accounts to offset the larger expenses, or start with a cushion in their shared account for any overages that might occur.`
    }
  ]
};

export const MarriageLawData = {
  title: 'Marriage law 101',
  disclaimer: `Disclaimer: This section is for general informational purposes only. Please don’t
  see this as legal advice: we aren’t lawyers, nor do we pretend to be. We’ve relied
  on information from various sources believed to be reliable, but we can’t swear by
  the accuracy and completeness of that information.`,
  heading: `There are a few things to be aware of when you're married, especially from a legal
  perspective. Below is a quick rundown on how the law treats your assets and debt.`,
  body: [
    {
      title: `How does the law see our money?`,
      body: `If you're married, the legal view of your assets depends on your state of residency - whether you live in a common law or community property state. Most states are common law property states; however, some states (such as California, Texas, and Washington) will treat the income you generate as a married couple as combined and owned equally (aka community property). This means that even if you choose to <a style="color:#195F53; text-decoration:underline;" href="https://www.askzeta.com/magazine/articles/married-separate-finances-financial-infidelity/?utm_medium=content&utm_source=combining-finances-guide">separate your finances</a>, you and your significant other will have a claim to any earned assets/debts in the event of a divorce. You can learn more about marital law and finances <a style="color:#195F53; text-decoration:underline;" href="https://family.findlaw.com/marriage/what-s-mine-is-mine-what-s-yours-is-mine-who-owns-what-in.html" target="_blank">here</a>. 
      <br />
      <br />
      All other states typically follow "equitable distribution" which means that a judge will decide what is fair rather than splitting your assets down the middle. Usually, that split will assign a larger percentage to the higher earning spouse and a lower one to the lower earning spouse. You can find more information about equitable distribution <a style="color:#195F53; text-decoration:underline;" href="https://family.findlaw.com/divorce/divorce-property-division-faq.html" target="_blank">here</a>. 
      <br />
      <br />
      There may be exceptions to the laws outlined above if you've signed a pre or post nuptial
      (see below).`
    }
  ]
};

export const NuptialData = {
  title: `Should we get a pre/post nuptial agreement?`,
  description: `Generally, if you or your partner have a strong opinion on how your assets should be divided in the event of a separation, you should consider a pre or post nuptial agreement. Some couples don’t like these agreements because they feel like they can create an “out-clause” or make it easier to break up. While there are pros and cons to be argued either way (we wrote about them <a style="color:#195F53; text-decoration:underline;" href="https://www.askzeta.com/magazine/articles/should-you-get-a-prenup">here</a>), at Zeta we believe that the conversations these agreements drive have inherent value in and of themselves.`,
  subTitle: `These are a few situations where pre/post nuptial can be especially valuable:`,
  body: [
    {
      body: ` &bull; If one partner is bringing significantly different assets into a relationship 
      <br />
      <br />
        &bull; If one partner is projected to inherit a large sum of money 
      <br />
      <br />
        &bull; If one or both partners are the child of divorced parents
      <br />
      <br />
        &bull; If one or both partners have a history of infidelity
      `
    }
  ],
  learnMore: `If you’re interested in learning more, you can see an example of a prenuptial agreement and learn more <a style="color:#195F53; text-decoration:underline;" href="https://legaltemplates.net/form/prenuptial-agreement/" target="_blank">here</a>.`,
  final: `Woah, you made it through this comprehensive guide to combining finances — congrats! We hope you leave feeling empowered to have conversations as a couple about the merge model that makes the most sense for you and your financial goals, and that you know how to start making progress on building that system. Good luck!`
};

export const CheatSheetData = {
  title: `Legal Terms Cheat Sheet`,
  subtitleOne: `Common Law`,
  bodyOne: `A term used to determine the ownership of marital property (property acquired during marriage). In states that follow the common law system, property acquired by one member of a married couple is owned completely and solely by that person.`,
  subtitleTwo: `Community Property`,
  bodyTwo: `Community property states follow the rule that all assets acquired during the marriage are considered "community property,” meaning all assets and liabilities are owned equally (50/50). This marital property includes earnings, all property bought with those earnings, and all debts accrued during the marriage. Community property begins at the marriage and ends when the couple physically separates with the intention of not continuing the marriage. Community property includes: money either spouse earned during the marriage, things bought with money either spouse earned during the marriage, and separate property that has become so mixed with community property that it can't be identified.`
};

export const CheatSheetDataTwo = {
  title: `Legal Terms Cheat Sheet`,
  subtitleOne: `Separate property`,
  bodyOne: `Any assets acquired before or after the marriage are considered separate property, and are owned only by that original owner. Separate property includes: property owned by just one spouse before the marriage, property given to just one spouse before or during the marriage, property inherited by just one spouse.`,
  subtitleTwo: `Pre/post nuptial agreement`,
  bodyTwo: `An agreement made between two people before or after marrying that establishes rights to property and support in the event of divorce or death.`
};

export const DisclaimerData = {
  title: 'Disclaimer',
  body: `This guide is for general informational purposes only. Please don’t see this as legal or investment advice; we aren’t lawyers or registered financial advisors, nor do we pretend to be. We’ve relied on information from various sources believed to be reliable but cannot swear by the accuracy and completeness of that information. By using this website, you recognize that the content being presented is provided for informational purposes only and agree to our Terms of Use and Privacy Policy.`
};

export const sectionData = [
  {
    section: 0,
    href: '#things-to-consider'
  },
  {
    section: 1,
    href: '#example-couple'
  },
  {
    section: 2,
    href: '#all-in-it'
  },
  {
    section: 3,
    href: '#yours-mine-ours'
  },
  {
    section: 4,
    href: '#keeping-it-separate'
  },
  {
    section: 5,
    href: '#single-life-income'
  },
  {
    section: 6,
    href: '#split-expenses'
  },
  {
    section: 7,
    href: '#marriage-law'
  },
  {
    section: 8,
    href: '#pre-post-nuptial'
  }
];
